import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TuneIcon from "@mui/icons-material/Tune";
import type { CanvasTab, DeviceMode } from "@/types/types";
import PreviewDesignDropdown from "./PreviewDesignDropdown";

interface CanvasToolbarProps {
  activeTab: CanvasTab;
  onTabChange: (tab: CanvasTab) => void;
  deviceMode: DeviceMode;
  onDeviceChange: (mode: DeviceMode) => void;
  drawerOpen: boolean;
  onCustomizeToggle: () => void;
  onEnterFullscreen: () => void;
  /** Manual-send mode: GfG isn't emailing, so the Email Design tab is locked. */
  emailLocked?: boolean;
  /** Clicking the LOCKED Email Design tab surfaces the helper tip instead of switching tabs. */
  onLockedEmailClick?: () => void;
}

const TABS: { id: CanvasTab; label: string }[] = [
  { id: "email", label: "Email Design" },
  { id: "unboxing", label: "Unboxing" },
  { id: "landing", label: "Landing Page" },
];

export default function CanvasToolbar({
  activeTab,
  onTabChange,
  deviceMode,
  onDeviceChange,
  drawerOpen,
  onCustomizeToggle,
  onEnterFullscreen,
  emailLocked = false,
  onLockedEmailClick,
}: CanvasToolbarProps) {
  return (
    <div
      className="flex items-center justify-between px-5 shrink-0 bg-white relative"
      style={{ borderBottom: "1px solid var(--color-card-border)", height: 48 }}
    >
      {/* Canvas tabs */}
      <div className="flex items-center gap-0">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const locked = tab.id === "email" && emailLocked;
          return (
            <button
              key={tab.id}
              id={`tour-tab-${tab.id}`}
              type="button"
              // The locked Email tab stays clickable, but the click surfaces the helper tip
              // (why email is off + how to turn it on) instead of switching to the inert canvas.
              onClick={() => (locked ? onLockedEmailClick?.() : onTabChange(tab.id))}
              aria-disabled={locked || undefined}
              title={locked ? "Email is off — you're distributing gift links manually." : undefined}
              className={[
                "px-5 py-3 font-lato text-[13px] font-semibold tracking-[0.3px] border-b-2 transition-colors inline-flex items-center gap-1.5",
                locked
                  ? "border-transparent text-dark-dust opacity-60 cursor-not-allowed"
                  : isActive
                    ? "border-rodeo-black text-rodeo-black"
                    : "border-transparent text-graphite hover:text-rodeo-black hover:border-smoke",
              ].join(" ")}
            >
              {tab.label}
              {locked && <LockOutlinedIcon sx={{ fontSize: 14 }} />}
            </button>
          );
        })}
      </div>

      {/* Device toggle — centered */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1">
        <button
          type="button"
          onClick={() => onDeviceChange("desktop")}
          title="Desktop"
          className={[
            "w-8 h-8 flex items-center justify-center rounded-sm transition-all border",
            deviceMode === "desktop"
              ? "border-dark-dust text-dark-dust bg-white"
              : "border-smoke text-graphite bg-white hover:border-dark-dust hover:text-dark-dust",
          ].join(" ")}
        >
          <DesktopWindowsOutlinedIcon sx={{ fontSize: 18 }} />
        </button>
        <button
          type="button"
          onClick={() => onDeviceChange("mobile")}
          title="Mobile"
          className={[
            "w-8 h-8 flex items-center justify-center rounded-sm transition-all border",
            deviceMode === "mobile"
              ? "border-dark-dust text-dark-dust bg-white"
              : "border-smoke text-graphite bg-white hover:border-dark-dust hover:text-dark-dust",
          ].join(" ")}
        >
          <SmartphoneIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      {/* Right cluster: Preview dropdown + Customize */}
      <div className="flex items-center gap-2">
        <PreviewDesignDropdown onEnterFullscreen={onEnterFullscreen} />

        <button
          id="tour-customize-btn"
          type="button"
          onClick={onCustomizeToggle}
          className={[
            "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-btn border font-lato text-[12px] font-semibold transition-all",
            drawerOpen
              ? "border-rodeo-black text-rodeo-black bg-wax"
              : "border-smoke text-graphite bg-white hover:border-dark-dust hover:text-dark-dust",
          ].join(" ")}
          title="Customize email, unboxing, and landing page"
        >
          <TuneIcon sx={{ fontSize: 16 }} />
          Customize
        </button>
      </div>
    </div>
  );
}
